import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { type ComputedRef, computed, type MaybeRefOrGetter, toValue } from "vue";
import { request } from "@/shared/http/client";
import type {
	AutoTopUpPatch,
	AutoTopUpState,
	CeilingPatch,
	ConsumptionPage,
	CreditEstimate,
	CreditsState,
	LedgerPage,
} from "./credit.types";

/**
 * The wallet and its statement.
 *
 * Separate from `use-billing` because these are read on a hot path and answer a
 * different question: how much quota is left, and where it went. Nothing here
 * returns money — the customer's credit routes do not carry BRL, USD, a model
 * name or a margin, and there is no field on these types to draw one from.
 */
export function useCredits(enabled: MaybeRefOrGetter<boolean> = true) {
	return useQuery({
		queryKey: ["credits", "me"] as const,
		queryFn: () => request<CreditsState>("/v3/credits"),
		enabled: () => toValue(enabled),
		staleTime: 15_000,
	});
}

/**
 * O preço de UMA resposta do atendente, em créditos — do servidor, nunca de uma
 * constante (ver `smartAnswers` em `money.ts`): o nível inteligente quando a
 * área cobra por nível, a resposta típica deste workspace quando cobra pelo
 * custo. `null` enquanto chega, ou pelo custo sem histórico; quem mostra
 * "≈ N respostas" cala a linha nesse caso. Mesma consulta `["credits", "me"]`.
 */
export function useAnswerPrice(): ComputedRef<number | null> {
	const { data } = useCredits();
	return computed(() => data.value?.prices?.perAnswer ?? null);
}

/**
 * O "≈ N respostas" vem do uso RECENTE deste workspace (a mediana de 30 dias) quando
 * o atendimento cobra pelo custo — e muda de uma visita para outra. A frase diz isso.
 */
export function useAnswerFromUsage(): ComputedRef<boolean> {
	const { data } = useCredits();
	return computed(() => data.value?.prices?.turn.attendant.by === "cost");
}

/**
 * "Avise em X, pare em Y" — the owner's own ceiling (F4).
 *
 * The WHOLE body travels every time, nulls included: a partial patch would make
 * "clear the stop" and "forgot the stop" the same request.
 */
export function useSetCeiling() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (patch: CeilingPatch) =>
			request<CreditsState>("/v3/credits/ceiling", { method: "PATCH", body: patch }),
		onSuccess: () => void client.invalidateQueries({ queryKey: ["credits"] }),
	});
}

/**
 * O EXCEDENTE AUTOMÁTICO: um interruptor, e o teto fica onde já estava.
 *
 * Invalida `credits` INTEIRO e não só a si mesmo: ligar com o saldo a zero faz
 * o servidor comprar um pacote na hora, e o saldo, a contagem de recargas e a
 * data de renovação mudam todos no mesmo pedido. Uma invalidação estreita
 * deixaria o medidor a dizer zero ao lado de um cartão a dizer "recarregado".
 */
export function useSetAutoTopUp() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (patch: AutoTopUpPatch) =>
			request<AutoTopUpState>("/v3/credits/auto-topup", { method: "PATCH", body: patch }),
		onSuccess: () => void client.invalidateQueries({ queryKey: ["credits"] }),
	});
}

/**
 * The informative estimate before a mission (plan §5): "deve usar ~30 créditos".
 * Information, never a quote — the mission still pays per turn, by tier.
 */
export function useCreditEstimate(
	input: MaybeRefOrGetter<{ tier: string; steps: number }>,
	enabled: MaybeRefOrGetter<boolean> = true,
) {
	return useQuery({
		queryKey: ["credits", "estimate", () => toValue(input)] as const,
		queryFn: () => {
			const { tier, steps } = toValue(input);
			return request<CreditEstimate>(`/v3/credits/estimate?tier=${tier}&steps=${steps}`);
		},
		enabled: () => toValue(enabled),
		staleTime: 300_000,
	});
}

export interface LedgerFilters {
	readonly from: string;
	readonly to: string;
	readonly kind: string;
	/** A kind the SERVER leaves out, so the page and its total agree. */
	readonly exceptKind?: string;
	readonly page: number;
	readonly pageSize: number;
}

/**
 * The ENTRIES statement — renewals, bonuses, adjustments, packs, reversals.
 *
 * The filters are in the key. They have to be: with a fixed key, page two and a
 * narrowed range would both read page one out of the cache and the screen would
 * quietly stop obeying its own controls.
 */
export function useCreditLedger(
	filters: MaybeRefOrGetter<LedgerFilters>,
	enabled: MaybeRefOrGetter<boolean> = true,
) {
	return useQuery({
		queryKey: ["credits", "ledger", () => toValue(filters)] as const,
		queryFn: () => request<LedgerPage>(`/v3/credits/ledger?${searchOf(toValue(filters))}`),
		enabled: () => toValue(enabled),
		placeholderData: (previous) => previous,
	});
}

export interface ConsumptionFilters {
	readonly from: string;
	readonly to: string;
	readonly source: string;
	readonly page: number;
	readonly pageSize: number;
}

/** The CONSUMPTION statement — one line per AI call, priced in credits. */
export function useConsumption(
	filters: MaybeRefOrGetter<ConsumptionFilters>,
	enabled: MaybeRefOrGetter<boolean> = true,
) {
	return useQuery({
		queryKey: ["credits", "consumption", () => toValue(filters)] as const,
		queryFn: () =>
			request<ConsumptionPage>(`/v3/credits/consumption?${searchOf(toValue(filters))}`),
		enabled: () => toValue(enabled),
		placeholderData: (previous) => previous,
	});
}

/**
 * Filters to a query string, dropping the empty ones.
 *
 * An empty `kind=` is not the same request as no `kind` at all — one of them is
 * a filter for the empty string — and the server should never have to guess
 * which the screen meant.
 */
function searchOf(filters: object): string {
	const query = new URLSearchParams();
	for (const [key, value] of Object.entries(filters)) {
		if (value === "" || value === null || value === undefined) continue;
		query.set(key, String(value));
	}
	return query.toString();
}

/**
 * The default range: the last thirty days, computed HERE and not in a component.
 *
 * Two screens ask the same question and a range assembled twice is a range that
 * one day differs by a day at one of its ends — which reads as data going
 * missing rather than as two different questions.
 */
export function defaultRange(days = 30): { from: string; to: string } {
	const to = new Date();
	const from = new Date(to.getTime() - days * 86_400_000);
	return { from: isoDay(from), to: isoDay(to) };
}

export function isoDay(at: Date): string {
	return at.toISOString().slice(0, 10);
}
